import React from 'react';
import { emptyState } from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormGroup } from 'reactstrap';

import {
	FormField,
	FIELD_TYPES,
	tcFormValidators,
} from '@techchange/components';
import { appPropTypes, useLanguageContext } from '@techchange/components';
import { messages } from '../../../../i18n';

import { RESOURCE_TYPES } from '../../../../constants';
import ContentEditor from '../../ContentEditor';
import {
	getInitialLanguage,
	useResourceFormData,
	RESOURCE_FORM_FIELDS,
} from '../../hooks/useResourceFormData';
import { RESOURCE_SLIDE_EDITOR_CONFIG } from '../../utils/constants';
import { contentBuilderPropTypes, initialValuesShape } from '../../utils/propTypes';

import './ContentEditor.scss';

const propTypes = {

	...contentBuilderPropTypes.contentEditor,

	/**
	 * The type of content.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.SLIDE]).isRequired,

	/**
	 * The initial values of the form.
	 */
	initialValues: PropTypes.shape({

		...initialValuesShape,

		/**
		 * The Prosemirror content.
		 */
		content: appPropTypes.proseMirrorDocument,

	}).isRequired,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack.isRequired,

};

const DEFAULT_RESOURCE_CONTENT = {
	[RESOURCE_FORM_FIELDS.CONTENT]: emptyState,
	[RESOURCE_FORM_FIELDS.TITLE]: '',
};

const getInitialFormData = (props) => {

	const { content, title, width, resourceContents, deletedResourceContents } = props.initialValues;

	return {
		[RESOURCE_FORM_FIELDS.CONTENT]: content || emptyState,
		[RESOURCE_FORM_FIELDS.TITLE]: title || '',
		[RESOURCE_FORM_FIELDS.WIDTH]: width,
		[RESOURCE_FORM_FIELDS.LANGUAGE]: getInitialLanguage(props),
		resource_contents: resourceContents || [{
			[RESOURCE_FORM_FIELDS.LANGUAGE]: props.defaultLanguage,
			...DEFAULT_RESOURCE_CONTENT,
		}],
		deleted_resource_contents: deletedResourceContents || [],
	};

};

const getInitialInputValidation = () => ({
	[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
	resource_contents: [tcFormValidators.arrayContainingObjects(RESOURCE_FORM_FIELDS.LANGUAGE, {
		title: [tcFormValidators.required]
	})],
});

/**
 * Editor for "slide" resource types. Admins can makes changes to the
 * "title" and "content" fields for slide resources.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const SlideContentEditor = (props) => {

	const {
		id,
		type,
		filestack,
		onCancel,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const formProps = {
		...props,
		userLanguage: preferredLanguage,
		defaultLanguage,
	};

	const { formData, inputValidation, actions } = useResourceFormData(
		() => getInitialFormData(formProps),
		getInitialInputValidation,
		{ ...formProps, defaultResourceContent: DEFAULT_RESOURCE_CONTENT }
	);

	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const activeInputValidation = inputValidation?.resource_contents?.data?.[formData.language];

	const formFields = activeTranslation && (
		<>
			<FormGroup>
				<FormField
					type={FIELD_TYPES.TEXT}
					name={RESOURCE_FORM_FIELDS.TITLE}
					value={activeTranslation.title}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_SLIDE_FIELD_LABEL_TEXT_TITLE)}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_SLIDE_FIELD_PLACEHOLDER_TEXT_TITLE)}
					isInvalid={activeInputValidation?.title?.invalid}
					isLabelHidden
					isEditing
					isRequired
					onChange={actions.updateField} />
			</FormGroup>
			<FormGroup>
				<FormField
					// Trigger re-mount when the selected language changes
					key={formData.language}
					id={`resource-content-${id}`}
					type={FIELD_TYPES.TEXT_EDITOR}
					name={RESOURCE_FORM_FIELDS.CONTENT}
					value={activeTranslation.content}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_SLIDE_FIELD_LABEL_TEXT_CONTENT)}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_SLIDE_FIELD_PLACEHOLDER_TEXT_CONTENT)}
					config={RESOURCE_SLIDE_EDITOR_CONFIG}
					emojiSize="24px"
					innerClassName="text-editor-body"
					filestackSettings={filestack}
					isEditing
					isLabelHidden
					onChange={actions.updateField} />
			</FormGroup>
		</>
	);

	return (
		<ContentEditor
			id={id}
			type={type}
			formData={formData}
			inputValidation={inputValidation}
			actions={actions}
			formFields={formFields}
			onCancel={onCancel} />
	);

};

SlideContentEditor.propTypes = propTypes;

export default SlideContentEditor;
